import i18n from 'i18next';

/**
 * Day-first date formatting, with the separator the current UI language
 * actually uses: 21.10.2026 in Polish, 21/10/2026 in English.
 *
 * Read lazily (not captured at import time) so switching language in
 * Settings takes effect on the next render without a reload.
 */
export const isPolish = () => String(i18n.language || 'en').startsWith('pl');

export const dateLocale = () => (isPolish() ? 'pl-PL' : 'en-GB');

export const dateSep = () => (isPolish() ? '.' : '/');

// "dd.mm.yyyy" / "dd/mm/yyyy" — the hint shown in an empty date field.
export const datePattern = () => ['dd', 'mm', 'yyyy'].join(dateSep());

/** Any date-like value (Date, ISO string, "2026-10-21T...") -> Date or null. */
export const toDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(String(value).length <= 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** ISO-8601 week number (Monday-start) — the "tydzień" the site talks in. */
export const weekNo = (value) => {
  const d = toDate(value);
  if (!d) return null;
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Thursday of the current week decides the year the week belongs to.
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
};

/** "21.10.2026" / "21/10/2026" for a Date or ISO string. */
export const fmtDate = (value, empty = '') => {
  const d = toDate(value);
  if (!d) return empty;
  const s = dateSep();
  return [String(d.getDate()).padStart(2, '0'), String(d.getMonth() + 1).padStart(2, '0'), d.getFullYear()].join(s);
};

/** "21.10.2026 W43" as plain text — for exports, filenames' siblings and
 *  anywhere a string (not JSX) is needed. */
export const fmtDateWeek = (value, empty = '') => {
  const d = toDate(value);
  if (!d) return empty;
  return `${fmtDate(d)} W${weekNo(d)}`;
};

/** "21.10.2026 14:30" — date plus time, day-first. */
export const fmtDateTime = (value, empty = '') => {
  const d = toDate(value);
  if (!d) return empty;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${fmtDate(d)} ${hh}:${mm}`;
};

/** "21.10.2026 14:30 W43" — plain text for timestamps in lists and logs. */
export const fmtDateTimeWeek = (value, empty = '') => {
  const d = toDate(value);
  if (!d) return empty;
  return `${fmtDateTime(d)} W${weekNo(d)}`;
};
